import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, takeLatest } from 'redux-saga/effects'

import { actions as drawingDataActions, types as drawingDataTypes } from '../reducers/drawingData'

import { getCurrentClubId } from './network'
import * as snack from './snackMessage'

import { apolloClient } from "@fe/business/src/apollo"
import { DELETE_CLUB_TRACKING_GROUPS } from "./mutations/drawingData"

const HOLE_DRAWING_QUERY = gql`
  query HoleDrawing($holeId: ID!) {
    holeDrawing(holeId: $holeId) {
      id
      courseId
      name
      description
      guideline
      par
      handicap
      order
      greenDataPosition
      createdAt
      updatedAt
      trackingGroupIds
      trackingGroups {
        id
        elementName
        geometryType
        groupName
        updatedAt
        teeName
        teeColor
        groupText
        attributes
        coordinates
      }
      rotation {
        wgs84source {
          x
          y
        }
        wgs84dest {
          x
          y
        }
      }
      boundary {
        boundary
      }
    }
  }
`

const toSnakeCaseTrackingGroup = (trackingGroup) => ({
  id: _.get('id', trackingGroup),
  element_name: _.get('elementName', trackingGroup),
  geometry_type: _.get('geometryType', trackingGroup),
  group_name: _.get('groupName', trackingGroup),
  updated_at: _.get('updatedAt', trackingGroup),
  tee_name: _.get('teeName', trackingGroup),
  tee_color: _.get('teeColor', trackingGroup),
  group_text: _.get('groupText', trackingGroup),
  attributes: _.get('attributes', trackingGroup),
  coordinates: _.get('coordinates', trackingGroup)
})

export function * loadDrawingDataEffect (holeId) {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: HOLE_DRAWING_QUERY,
      variables: { holeId },
      fetchPolicy: 'network-only'
    })
    const drawing = _.get('data.holeDrawing', response)
    return {
      id: _.get('id', drawing),
      course_id: _.get('courseId', drawing),
      name: _.get('name', drawing),
      description: _.get('description', drawing),
      guideline: _.get('guideline', drawing),
      par: _.get('par', drawing),
      handicap: _.get('handicap', drawing),
      order: _.get('order', drawing),
      green_data_position: _.get('greenDataPosition', drawing),
      created_at: _.get('createdAt', drawing),
      updated_at: _.get('updatedAt', drawing),
      tracking_group_ids: _.get('trackingGroupIds', drawing),
      tracking_groups: _.map(toSnakeCaseTrackingGroup, _.getOr([], 'trackingGroups', drawing)),
      rotation: _.get('rotation', drawing),
      boundary: _.get('boundary', drawing)
    }
  } catch (error) {
    return { error }
  }
}

const SAVE_HOLE_DRAWING_MUTATION = gql`
  mutation SaveHoleDrawing($input: SaveHoleDrawingInput!) {
    saveHoleDrawing(input: $input)
  }
`

const toDrawingElementInput = (element) => ({
  id: _.get('id', element),
  coordinates: _.get('coordinates', element),
  elementName: _.get('elementName', element) ?? _.get('element_name', element),
  attributes: _.get('attributes', element),
  text: _.get('text', element)
})

export function * saveDrawingDataEffect (data) {
  try {
    const input = {
      clubId: _.get('clubId', data) ?? _.get('clubid', data),
      holeId: _.get('holeId', data) ?? _.get('holeid', data),
      context: _.get('context', data),
      boundary: _.map(toDrawingElementInput, _.getOr([], 'boundary', data)),
      newTrackingGroups: _.map(toDrawingElementInput, _.get('newTrackingGroups', data) ?? _.getOr([], 'new_tracking_groups', data)),
      trackingGroups: _.map(toDrawingElementInput, _.get('trackingGroups', data) ?? _.getOr([], 'tracking_groups', data)),
      removedTrackingGroups: _.map(String, _.get('removedTrackingGroups', data) ?? _.getOr([], 'removed_tracking_groups', data))
    }
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: SAVE_HOLE_DRAWING_MUTATION,
      variables: { input }
    })
    return _.get('data.saveHoleDrawing', response)
  } catch (error) {
    return { error }
  }
}

const CLUB_TRACKING_GROUPS_QUERY = gql`
  query ClubTrackingGroups($clubId: ID!) {
    clubTrackingGroups(clubId: $clubId) {
      id
      area
      isValid
      createdAt
      updatedAt
      elementName
      coordinates {
        longitude
        latitude
      }
      customAttributes
    }
  }
`

export function * loadTrackingsEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_TRACKING_GROUPS_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.clubTrackingGroups', response)
  } catch (error) {
    return { error }
  }
}

export function* deleteTrackingsEffect({ payload }) {
  const { clubId } = payload;

  try {
    yield apolloClient.mutate({
      mutation: DELETE_CLUB_TRACKING_GROUPS,
      variables: { clubId },
    });
  } catch (error) {
    throw error;
  }
}

const CLUB_TRACKING_GROUPS_COURSES_QUERY = gql`
  query ClubTrackingGroupsCourses($clubId: ID!) {
    clubTrackingGroupsCourses(clubId: $clubId) {
      trackingGroupIds
      courseId
      holeId
      order
    }
  }
`

export function * loadTrackingGroupCoursesEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_TRACKING_GROUPS_COURSES_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.clubTrackingGroupsCourses', response)
  } catch (error) {
    return { error }
  }
}

const GRAPHICS_CONTEXTS_QUERY = gql`
  query GraphicsContexts {
    graphicsContexts {
      name
      elements
    }
  }
`

export function * getContextsEffect () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: GRAPHICS_CONTEXTS_QUERY,
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.graphicsContexts', response)
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

function * loadTrackingsFlow () {
  const result = yield call(loadTrackingsEffect)
  const error = _.get('error', result)
  if (error) {
    yield put(drawingDataActions.loadTrackingsError(error))
  } else {
    yield put(drawingDataActions.loadTrackingsSuccess(result))
  }
}

function* deleteTrackingsFlow({ payload }) {
  try {
    yield call(deleteTrackingsEffect, { payload })
    yield put(drawingDataActions.deleteTrackingsSuccess())
    yield put(drawingDataActions.loadTrackings())
  } catch (error) {
    yield put(drawingDataActions.deleteTrackingsError(error))
    console.error("deleteTrackingsFlow error", error)
  }
}

function * loadTrackingGroupCoursesFlow () {
  const result = yield call(loadTrackingGroupCoursesEffect)
  const error = _.get('error', result)
  if (error) {
    return yield put(drawingDataActions.loadTrackingGroupCoursesError(error))
  }
  const massagedData = _.flow(
    _.groupBy('courseId'),
    _.omitBy((holes) => (_.size(holes) < 2)),
    _.mapValues(_.keyBy('holeId'))
  )(result)
  return yield put(drawingDataActions.loadTrackingGroupCoursesSuccess(massagedData))
}

function * loadDrawingDataFlow ({ payload: holeId }) {
  const result = yield call(loadDrawingDataEffect, holeId)
  const error = _.get('error', result)
  if (error) {
    yield put(drawingDataActions.loadError(error))
  } else {
    yield put(drawingDataActions.loadSuccess(result))
  }
}

function * saveDrawingDataFlow ({ payload: data }) {
  const result = yield call(saveDrawingDataEffect, data)
  const error = _.get('error', result)
  if (error) {
    yield put(drawingDataActions.saveError(error))
  } else {
    yield put(drawingDataActions.saveSuccess(result))
    yield put(drawingDataActions.loadRequest(_.get('holeId', data) ?? _.get('holeid', data)))
    yield snack.setMessage('Saved!')
  }
}

function * getContextsFlow () {
  const result = yield call(getContextsEffect)
  const error = _.get('error', result)
  if (error) {
    yield put(drawingDataActions.getContextsError(error))
  } else {
    yield put(drawingDataActions.getContextsSuccess(result))
  }
}

export default function * () {
  return yield all([
    takeLatest(drawingDataTypes.LOAD_REQUEST, loadDrawingDataFlow),
    takeLatest(drawingDataTypes.SAVE_REQUEST, saveDrawingDataFlow),
    takeLatest(drawingDataTypes.LOAD_TRACKINGS, loadTrackingsFlow),
    takeLatest(drawingDataTypes.LOAD_TRACKING_GROUP_COURSES, loadTrackingGroupCoursesFlow),
    takeLatest(drawingDataTypes.GET_CONTEXTS, getContextsFlow),
    takeLatest(drawingDataTypes.DELETE_TRACKINGS_REQUEST, deleteTrackingsFlow),
  ])
}
